import { useEffect, useState } from "react";

import { BillingFacturationNetResponse } from "@/Billing/Api/FacturationNet/Dto/Responses/FacNetBillLightResponse";
import { FacturationNetManager } from "@/Billing/Services/FacturationNet/FacturationNetManager";
import { ExternalBillingClaimDto } from "@/Common/Api/ExternalBilling/Dto/ExternalBillingClaimDto";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import { BillingProviderHelper } from "@/Common/Services/ExternalBilling/BillingProvider";
import { ExternalBillingManager } from "@/Common/Services/ExternalBilling/ExternalBillingManager";
import { LuxonDateTimeParser } from "@/Common/Utilities/DateAndTime/LuxonDateTimeParser";
import { MedfarBillLightPreview } from "@/Patient/Api/MedfarBilling/Dto/Responses/FacturationReponse";

import BillsSummarySectionStyle from "./BillsSummarySection.module.css";

interface BillsSummarySectionProps {
    encounterId: string;
    patientId: string;
    medfarBills: MedfarBillLightPreview[];
    facturationBills: BillingFacturationNetResponse[];
    externalClaims: ExternalBillingClaimDto[];
    isReadonly?: boolean;
}

function BillsSummarySection(props: BillsSummarySectionProps): JSX.Element {
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => setIsLoading(false), [props.encounterId]);

    // open claims
    const openClaimWindowAsync = async (claim: ExternalBillingClaimDto): Promise<void> => {
        if (BillingProviderHelper.shouldUseLegacyMedfarBilling(claim.BillingProvider)) {
            openMedfarBill(claim.ProviderClaimId);
            return;
        }

        if (BillingProviderHelper.shouldUseLegacyFacturationNetBilling(claim.BillingProvider)) {
            openFacturationNetBill(claim.ProviderClaimId);
            return;
        }

        await ExternalBillingManager.openClaimWindowAsync(claim, () => claimReload(props.encounterId));
    };

    const openMedfarBill = (billId: string): void => {
        sessionStore.dispatch(
            LocalPatientOperations.MedfarBilling.OpenExistingBill,
            { billId: billId }
        );
    };

    const openFacturationNetBill = (billId: string): void => {
        FacturationNetManager.openBill(billId);
    };

    // cancel claims
    const cancelClaimAsync = async (claim: ExternalBillingClaimDto): Promise<void> => {
        if (BillingProviderHelper.shouldUseLegacyMedfarBilling(claim.BillingProvider)) {
            deleteMedfarBill(claim.ProviderClaimId);
            return;
        }

        if (BillingProviderHelper.shouldUseLegacyFacturationNetBilling(claim.BillingProvider)) {
            deleteFacturationNetBill(claim.ProviderClaimId, props.patientId);
            return;
        }

        const isSuccess = await ExternalBillingManager.cancelClaimAsync(
            claim.EncounterId,
            claim.BillingProvider,
            claim.ProviderClaimId
        );

        if (!isSuccess)
            return;

        const noteModel = patientStore.PatientMedicalNoteModels[props.patientId];

        // return if the encounter has changed
        if (noteModel.get("Encounter").Id !== claim.EncounterId)
            return;

        const filteredClaims = noteModel.get("ExternalBillingClaims").filterImmutable(p => p.ProviderClaimId != claim.ProviderClaimId);

        patientStore.PatientMedicalNoteModels[props.patientId] = noteModel.set("ExternalBillingClaims", filteredClaims);

        patientStore.EmitChangeToListeners();
    };

    const deleteMedfarBill = (billId: string): void => {
        sessionStore.dispatch(
            LocalPatientOperations.MedfarBilling.DeleteBill,
            { billId: billId }
        );
    };

    const deleteFacturationNetBill = (claimId: string, patientId: string): void => {
        FacturationNetManager.deleteBill(
            claimId,
            () => {
            // On Success
                let noteModel = patientStore.PatientMedicalNoteModels[patientId];
                noteModel = noteModel.set("FacturationNetBills", noteModel.get("FacturationNetBills").filterImmutable(x => x.ClaimId != claimId));

                patientStore.PatientMedicalNoteModels[patientId] = noteModel;
                patientStore.EmitChangeToListeners();
            }
        );
    };

    const claimReload = async (encounterId: string): Promise<void> => {
        const noteEncounterId = patientStore.PatientMedicalNoteModels[props.patientId].get("Encounter").Id;
        if (noteEncounterId !== encounterId)
            return;

        setIsLoading(true);

        await ExternalBillingManager.getClaimsByEncounterIdsAsync([encounterId], true)
            .then((updatedClaims: ExternalBillingClaimDto[]) => {
                if (updatedClaims) {
                    patientStore.PatientMedicalNoteModels[props.patientId] = patientStore.PatientMedicalNoteModels[props.patientId].set("ExternalBillingClaims", updatedClaims.toImmutable());
                    patientStore.EmitChangeToListeners();
                }
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    return (
        <div className={props.isReadonly ? "no-hover" : ""}>
            {!isArrayNullOrUndefinedOrEmpty(props.medfarBills) && (
                props.medfarBills.map(bill => {
                    const islocked = bill.BatchId != null;
                    const formattedBillDate = LuxonDateTimeParser.parse(bill?.BillDate)?.toFormat("yyyy/MM/dd @ HH:mm:ss") ?? "";

                    return (
                        <div key={bill.FacturationRequestId} data-cy="bill-container">
                            <a onClick={() => openMedfarBill(bill.FacturationRequestId)}>
                                {`${formattedBillDate ? (formattedBillDate + " , ") : ""}(${bill.BillSummary})`}
                            </a>
                            {!islocked && !props.isReadonly && (
                                <span
                                    className="glyphicon glyphicon-remove red-text pointer"
                                    onClick={() => deleteMedfarBill(bill.FacturationRequestId)}
                                />
                            )}
                        </div>
                    );
                })
            )}
            {!isArrayNullOrUndefinedOrEmpty(props.facturationBills) && (
                props.facturationBills.map(bill => (
                    <div key={`FacNet_${bill.ClaimId}`} data-cy="bill-container">
                        <a onClick={() => openFacturationNetBill(bill.ClaimId)}>{BillingFacturationNetResponse.getBillDisplayString(bill)}</a>
                        {BillingFacturationNetResponse.canCancelBill(bill) && !props.isReadonly && (
                            <span
                                className="glyphicon glyphicon-remove red-text pointer margin-xs-left"
                                onClick={() => deleteFacturationNetBill(bill.ClaimId, props.patientId)}
                            />
                        )}
                    </div>
                ))
            )}
            {!isArrayNullOrUndefinedOrEmpty(props.externalClaims) && (
                <div className="flex-container">
                    {props.externalClaims.sortByProperty(p => p.DateCreated).map(claim => (
                        <div key={`${claim.BillingProvider}_${claim.ProviderClaimId}`} data-cy="bill-container" style={{ "marginRight": "10px" }}>
                            {ExternalBillingManager.canEmployeeViewClaimDetails(sessionStore.getCurrentEmployee(), claim)
                                ? <a onClick={async () => await openClaimWindowAsync(claim)}> {ExternalBillingManager.getDisplayText(claim)}</a>
                                : <span>{ExternalBillingManager.getDisplayText(claim)}</span>
                            }
                            {claim.CanBeCancelled && !props.isReadonly && (
                                <span
                                    className="glyphicon glyphicon-remove red-text pointer margin-xs-left"
                                    onClick={async () => await cancelClaimAsync(claim)}
                                />
                            )}
                        </div>
                    ))}
                    {!isLoading && (
                        <div
                            className={"pointer a text-center " + BillsSummarySectionStyle.pointerContainer}
                            onClick={() => claimReload(props.encounterId)}
                        >
                            <div
                                className={BillsSummarySectionStyle.refreshBackground}
                            />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export { BillsSummarySection };
